import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Close as CloseIcon,
  Update as UpdateIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useBulkUpdateBOMHardwareByFixedAmountMutation,
  useBulkUpdateBOMHardwareByPercentageMutation,
  useBulkUpdateBundleCostsByFixedAmountMutation,
  useBulkUpdateBundleCostsByPercentageMutation,
} from './price-management-api-slice';
import { BOMHardware, BundleCost, MaterialGroupOption, PriceVersion } from './types';

export interface BulkUpdateModalProps {
  open: boolean;
  version: PriceVersion;
  materialGroupOptions: MaterialGroupOption[];
  bundleCosts: BundleCost[];
  bomHardware: BOMHardware[];
  onClose: () => void;
  onSuccess: (updatedCount: number, dataType: string, cappedCount: number) => void;
}

type DataType = 'bundle-costs' | 'bom-hardware';
type AdjustmentMethod = 'percentage' | 'fixed';

const PERCENTAGE_MIN = -50;
const PERCENTAGE_MAX = 100;
const FIXED_MIN = -1000;
const FIXED_MAX = 1000;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

/**
 * Bulk Update Modal (US5)
 * Apply percentage or fixed-cost adjustments to bundle costs or BOM hardware.
 */
const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({
  open,
  version,
  materialGroupOptions,
  bundleCosts,
  bomHardware,
  onClose,
  onSuccess,
}) => {
  const [dataType, setDataType] = useState<DataType>('bundle-costs');
  const [method, setMethod] = useState<AdjustmentMethod>('percentage');
  const [percentageValue, setPercentageValue] = useState('');
  const [fixedValue, setFixedValue] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [inputError, setInputError] = useState('');

  useEffect(() => {
    if (open) {
      setDataType('bundle-costs');
      setMethod('percentage');
      setPercentageValue('');
      setFixedValue('');
      setSelectedGroups(new Set());
      setInputError('');
    }
  }, [open]);

  const [bulkUpdateBundleByPct, { isLoading: isBundlePctLoading, error: bundlePctError }] =
    useBulkUpdateBundleCostsByPercentageMutation();
  const [bulkUpdateBundleByFixed, { isLoading: isBundleFixedLoading, error: bundleFixedError }] =
    useBulkUpdateBundleCostsByFixedAmountMutation();
  const [bulkUpdateHwByPct, { isLoading: isHwPctLoading, error: hwPctError }] =
    useBulkUpdateBOMHardwareByPercentageMutation();
  const [bulkUpdateHwByFixed, { isLoading: isHwFixedLoading, error: hwFixedError }] =
    useBulkUpdateBOMHardwareByFixedAmountMutation();

  const isLoading =
    isBundlePctLoading || isBundleFixedLoading || isHwPctLoading || isHwFixedLoading;
  const mutationError = bundlePctError || bundleFixedError || hwPctError || hwFixedError;

  const hasNoRecords =
    dataType === 'bundle-costs' ? bundleCosts.length === 0 : bomHardware.length === 0;

  // Build group → item count map from actual loaded bundle cost data
  const groupCounts = useMemo(() => {
    const map = new Map<string, number>();
    bundleCosts.forEach(bc => {
      const g = bc.materialGroup || 'Other';
      map.set(g, (map.get(g) ?? 0) + 1);
    });
    return map;
  }, [bundleCosts]);

  // Groups ordered by sortOrder from reference table, filtered to only those with actual data
  const groupNames = useMemo(
    () =>
      materialGroupOptions
        .filter(g => groupCounts.has(g.groupName))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(g => g.groupName),
    [materialGroupOptions, groupCounts]
  );

  const handleSelectAll = () => setSelectedGroups(new Set(groupNames));
  const handleClearAll = () => setSelectedGroups(new Set());
  const toggleGroup = (name: string) =>
    setSelectedGroups(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const currentValue = method === 'percentage' ? percentageValue : fixedValue;
  const parsedValue = parseFloat(currentValue);
  const isValidNumber = currentValue !== '' && !isNaN(parsedValue) && parsedValue !== 0;

  const validateInput = (value: string): string => {
    const num = parseFloat(value);
    if (value === '' || isNaN(num)) return 'Please enter a value.';
    if (num === 0) return 'Value must be non-zero.';
    if (method === 'percentage') {
      if (num < PERCENTAGE_MIN || num > PERCENTAGE_MAX)
        return `Percentage must be between ${PERCENTAGE_MIN}% and +${PERCENTAGE_MAX}%.`;
    } else {
      if (num < FIXED_MIN || num > FIXED_MAX)
        return `Amount must be between -$${Math.abs(FIXED_MIN)} and +$${FIXED_MAX}.`;
    }
    return '';
  };

  // Compute live preview totals from loaded data
  const preview = useMemo(() => {
    if (!isValidNumber) return null;

    if (dataType === 'bundle-costs') {
      const inScope =
        selectedGroups.size === 0
          ? bundleCosts
          : bundleCosts.filter(bc => selectedGroups.has(bc.materialGroup));

      let cappedCount = 0;
      const currentTotal = inScope.reduce((sum, bc) => sum + bc.bundleCost, 0);
      const newTotal = inScope.reduce((sum, bc) => {
        const raw =
          method === 'percentage'
            ? bc.bundleCost * (1 + parsedValue / 100)
            : bc.bundleCost + parsedValue;
        if (raw < 0.01) cappedCount++;
        return sum + Math.max(0.01, raw);
      }, 0);
      const change = newTotal - currentTotal;
      const changePct = currentTotal !== 0 ? (change / currentTotal) * 100 : 0;
      return { count: inScope.length, currentTotal, newTotal, change, changePct, cappedCount };
    } else {
      let cappedCount = 0;
      const currentTotal = bomHardware.reduce((sum, bh) => sum + bh.costPerBox, 0);
      const newTotal = bomHardware.reduce((sum, bh) => {
        const raw =
          method === 'percentage'
            ? bh.costPerBox * (1 + parsedValue / 100)
            : bh.costPerBox + parsedValue;
        if (raw < 0.01) cappedCount++;
        return sum + Math.max(0.01, raw);
      }, 0);
      const change = newTotal - currentTotal;
      const changePct = currentTotal !== 0 ? (change / currentTotal) * 100 : 0;
      return { count: bomHardware.length, currentTotal, newTotal, change, changePct, cappedCount };
    }
  }, [isValidNumber, dataType, selectedGroups, bundleCosts, bomHardware, method, parsedValue]);

  const handleApply = async () => {
    const err = validateInput(currentValue);
    if (err) {
      setInputError(err);
      return;
    }
    setInputError('');

    try {
      let result: { updatedCount: number; cappedCount: number; message: string };

      if (dataType === 'bundle-costs') {
        if (selectedGroups.size > 1) {
          let totalCount = 0;
          let totalCapped = 0;
          for (const group of Array.from(selectedGroups)) {
            const r =
              method === 'percentage'
                ? await bulkUpdateBundleByPct({
                    versionId: version.id,
                    percentageChange: parsedValue,
                    materialGroup: group,
                  }).unwrap()
                : await bulkUpdateBundleByFixed({
                    versionId: version.id,
                    fixedAmount: parsedValue,
                    materialGroup: group,
                  }).unwrap();
            totalCount += r.updatedCount;
            totalCapped += r.cappedCount;
          }
          result = { updatedCount: totalCount, cappedCount: totalCapped, message: '' };
        } else {
          const materialGroup =
            selectedGroups.size === 1 ? Array.from(selectedGroups)[0] : undefined;
          result =
            method === 'percentage'
              ? await bulkUpdateBundleByPct({
                  versionId: version.id,
                  percentageChange: parsedValue,
                  materialGroup,
                }).unwrap()
              : await bulkUpdateBundleByFixed({
                  versionId: version.id,
                  fixedAmount: parsedValue,
                  materialGroup,
                }).unwrap();
        }
      } else {
        result =
          method === 'percentage'
            ? await bulkUpdateHwByPct({
                versionId: version.id,
                percentageChange: parsedValue,
              }).unwrap()
            : await bulkUpdateHwByFixed({
                versionId: version.id,
                fixedAmount: parsedValue,
              }).unwrap();
      }

      onSuccess(
        result.updatedCount,
        dataType === 'bundle-costs' ? 'bundle costs' : 'BOM hardware items',
        result.cappedCount
      );
      onClose();
    } catch {
      // error shown via mutationError
    }
  };

  const isPositive = isValidNumber && parsedValue > 0;
  const isNegative = isValidNumber && parsedValue < 0;

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UpdateIcon color="primary" />
            <Typography variant="h6" component="span">
              Bulk Update
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={isLoading} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Version {version.versionNumber}.0 (Draft)
          </Typography>
          <Chip
            icon={<WarningAmberIcon sx={{ fontSize: 14, color: '#fff' }} />}
            label="Draft version only"
            size="small"
            sx={{ bgcolor: '#f9a825', color: '#fff', fontWeight: 500, height: 22 }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Divider sx={{ mb: 2 }} />

        {/* Data type */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Select Data Type to Update:
        </Typography>
        <RadioGroup
          value={dataType}
          onChange={e => {
            setDataType(e.target.value as DataType);
            setInputError('');
          }}
          sx={{ mb: 0.5 }}
        >
          <FormControlLabel
            value="bundle-costs"
            control={<Radio size="small" />}
            label="Bundle Costs"
          />
          <FormControlLabel
            value="bom-hardware"
            control={<Radio size="small" />}
            label="BOM Hardware"
          />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {dataType === 'bundle-costs'
            ? 'Updates Bundle Cost field (affects Cost/Ft, Cost/Inch, etc.)'
            : 'Updates Cost Per Box field (affects Cost Per Item)'}
        </Typography>

        {hasNoRecords && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No {dataType === 'bundle-costs' ? 'bundle cost' : 'BOM hardware'} records exist in this
            version. Add records before using Bulk Update.
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Adjustment method */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Select Adjustment Method:
        </Typography>
        <RadioGroup
          value={method}
          onChange={e => {
            setMethod(e.target.value as AdjustmentMethod);
            setPercentageValue('');
            setFixedValue('');
            setInputError('');
          }}
          sx={{ mb: 1.5 }}
        >
          <FormControlLabel
            value="percentage"
            control={<Radio size="small" disabled={hasNoRecords} />}
            label="Percentage Adjustment"
            disabled={hasNoRecords}
          />
          <FormControlLabel
            value="fixed"
            control={<Radio size="small" disabled={hasNoRecords} />}
            label="Fixed Cost Adjustment"
            disabled={hasNoRecords}
          />
        </RadioGroup>

        {/* Input field */}
        {method === 'percentage' ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Adjustment Percentage:
            </Typography>
            <TextField
              type="number"
              value={percentageValue}
              disabled={hasNoRecords}
              onChange={e => {
                setPercentageValue(e.target.value);
                if (inputError) setInputError(validateInput(e.target.value));
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: PERCENTAGE_MIN, max: PERCENTAGE_MAX, step: 0.5 },
              }}
              error={!!inputError}
              helperText={
                inputError || 'Enter percentage to increase (positive) or decrease (negative)'
              }
              size="small"
              sx={{ width: 200 }}
            />
            {isValidNumber && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                {isPositive ? (
                  <ArrowUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
                ) : isNegative ? (
                  <ArrowDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
                ) : null}
                <Typography
                  variant="caption"
                  sx={{ color: isPositive ? 'success.main' : 'error.main', fontWeight: 500 }}
                >
                  {isPositive ? '+' : ''}
                  {parsedValue}% adjustment
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Fixed Cost Increase/Decrease:
            </Typography>
            <TextField
              type="number"
              value={fixedValue}
              disabled={hasNoRecords}
              onChange={e => {
                setFixedValue(e.target.value);
                if (inputError) setInputError(validateInput(e.target.value));
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                inputProps: { min: FIXED_MIN, max: FIXED_MAX, step: 0.01 },
              }}
              error={!!inputError}
              helperText={
                inputError ||
                `Enter dollar amount to add (positive) or subtract (negative). Range: -$${Math.abs(FIXED_MIN)} to +$${FIXED_MAX}`
              }
              size="small"
              sx={{ width: 220 }}
            />
            {isValidNumber && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                {isPositive ? (
                  <ArrowUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
                ) : isNegative ? (
                  <ArrowDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
                ) : null}
                <Typography
                  variant="caption"
                  sx={{ color: isPositive ? 'success.main' : 'error.main', fontWeight: 500 }}
                >
                  {isPositive ? '+' : ''}${parsedValue.toFixed(2)} per record
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Material group filter — bundle costs only */}
        {dataType === 'bundle-costs' && groupNames.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Filter by Material Groups (Optional):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button size="small" variant="outlined" onClick={handleClearAll}>
                  Clear All
                </Button>
              </Box>
            </Box>
            <FormGroup sx={{ maxHeight: 180, overflowY: 'auto', pl: 0.5 }}>
              {groupNames.map(name => (
                <FormControlLabel
                  key={name}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedGroups.has(name)}
                      onChange={() => toggleGroup(name)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {name} ({groupCounts.get(name) ?? 0} bundle costs)
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </>
        )}

        <Divider sx={{ mt: 2, mb: 2 }} />

        {/* Preview Changes */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Preview Changes
        </Typography>
        {preview ? (
          <Box sx={{ pl: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              · {preview.count}{' '}
              {dataType === 'bundle-costs' ? 'bundle costs' : 'BOM hardware items'} will be updated
            </Typography>
            <Typography variant="body2" color="text.secondary">
              · Current Total: {formatCurrency(preview.currentTotal)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              · New Total: {formatCurrency(preview.newTotal)}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ color: preview.change >= 0 ? 'success.main' : 'error.main' }}
            >
              · Change: {preview.change >= 0 ? '+' : ''}
              {formatCurrency(preview.change)} ({preview.changePct >= 0 ? '+' : ''}
              {preview.changePct.toFixed(1)}%)
            </Typography>
            {preview.cappedCount > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Note: {preview.cappedCount} record{preview.cappedCount !== 1 ? 's' : ''} capped at
                $0.01 minimum
              </Alert>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
            Enter an adjustment value above to preview changes.
          </Typography>
        )}

        {mutationError != null && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {typeof mutationError === 'object' && 'data' in (mutationError as object)
              ? String(
                  (mutationError as { data: { title?: string } }).data?.title ??
                    (mutationError as { data: unknown }).data
                )
              : 'An error occurred while applying the bulk update. Please try again.'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Tooltip
          title={
            hasNoRecords
              ? 'No records available to update'
              : !isValidNumber
                ? 'Enter a valid non-zero adjustment value to proceed'
                : ''
          }
        >
          <span>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={hasNoRecords || !isValidNumber || isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isLoading ? 'Updating...' : 'Apply Changes'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateModal;
